import { ChartDataClient, ChartDataRequestModel, ClientResponseModel, Connection, HolidayClient, HolidayModel, TopListClient, TopListDataRequestModel, TopListDataResponseModel } from "../../../../pure-javascript-api/ti-js-api";
import { HolidayManager } from "../../../pure-javascript-api/src/services/models/holiday/holiday.manager";
import { MarketingWebChartXAxisPlotLineComponent } from "../components/marketing-webchart-preview-x-axis-plot-line.component";
import { MarketingWebChartSummaryStatusLineComponent } from "../components/marketing-webchart-summary-status-line";
import { DateHelpers } from "../helpers/date-helpers";
import { FormatHelpers } from "../helpers/format-helpers";
import { ObjectHelpers } from "../helpers/object-helpers";
import { TimeframeHelper } from "../helpers/timeframe-helpers";
import { UrlHelpers } from "../helpers/url-helpers";
import { WebChartThemeHelpers } from "../helpers/webchart-theme-helpers";
import { JSApiPointToTiChartDataPointMapper } from "../js-api-mappers/data-point/js-api-point-to-ti-chart-point.mapper";
import { JSApiPointToTiIndicatorPointMapper } from "../js-api-mappers/data-point/js-api-point-to-ti-indicator-point.mapper";
import { JSApiPointToUpdateTiChartDataPointMapper } from "../js-api-mappers/data-point/js-api-point-to-update-ti-chart-point.mapper";
import { TimeFrameToChartTimeFrameMapper } from "../js-api-mappers/timeframe/time-frame-to-chart-time-frame.mapper";
import { IChartContainer } from "../models/base/chart-container.interface";
import { IChartDataService } from "../models/base/chart-data-service.interface";
import { ChartStyleColorModel } from "../models/chart-style/chart-style-color.model";
import { ChartStyleSettingsModel } from "../models/chart-style/chart-style-settings.model";
import { WebchartThemeManager } from "../models/chart-style/web-chart-theme.manager";
import { TIChartPointModel } from "../models/data-point/ti-chart-point.model";
import { TIIndicatorDataModel } from "../models/data-point/ti-indicator-data.model";
import { DrawEarningNotificationDataModel } from "../models/earning/draw-earning-notification-data.model";
import { ChartType } from "../models/enum/chart-type.enum";
import { ScrollMode } from "../models/enum/scroll-mode.enum";
import { Timeframe } from "../models/enum/timeframe.enum";
import { ChartMode, ChartTheme } from "../models/marketing-webchart/marketing-webchart-window-wrapper.model";
import { WebChartsMarketingContainer } from "../web-charts-marketing.container";
const color = require('../library/darken-color.js');
declare const TIJSAPI: any;
declare const window: any;

export class WebChartDataService implements IChartDataService {
    private subscribeInitChartDataCallbacks: Array<Function> = [];
    private subscribeInitChartIndicatorsDataCallbacks: Array<Function> = [];
    private subscribeInitChartComparisonsDataCallbacks: Array<Function> = [];
    private subscribeInitChartDrawingToolsCallbacks: Array<Function> = [];
    private subscribeUpdate10sRefreshChartDataCallbacks: Array<Function> = [];
    private subscribeUpdate10sRefreshIndicatorsDataCallbacks: Array<Function> = [];
    private subscribeUpdateChartHistoryDataCallbacks: Array<Function> = [];
    private subscribeUpdateHistoryIndicatorsDataCallbacks: Array<Function> = [];
    private subscribeUpdateChartHistoryComparisonDataCallbacks: Array<Function> = [];
    private subscribeRealtimeDataCallbacks: Array<Function> = [];
    private subscribeChartStyleDataCallbacks: Array<Function> = [];
    private subscribePnlDataCallbacks: Array<Function> = [];
    private subscribeCurrentPositionDataCallbacks: Array<Function> = [];
    // private subscribeTICompanyLogoDataCallbacks: Array<Function> = [];
    private subscribeChartRequirementsDataCallbacks: Array<Function> = [];
    private subscribeChartAllowBuyDataCallbacks: Array<Function> = [];
    private subscribeChartAllowSellDataCallbacks: Array<Function> = [];
    private subscribeCompanyLogoDataCallbacks: Array<Function> = [];
    private subscribeDrawTradeOrdersDataCallbacks: Array<Function> = [];
    private subscribeDrawOrderExecutionDataCallbacks: Array<Function> = [];
    private subscribePriceAlertsDataCallbacks: Array<Function> = [];
    private subscribePriceAlertsImageDataCallbacks: Array<Function> = [];
    private subscribeEarningNotificationDataCallbacks: Array<Function> = [];
    private subscribeHaltedNotificationDataCallbacks: Array<Function> = [];
    private subscribeSwingExitDataCallbacks: Array<Function> = [];
    private subscribSmartRiskDataCallbacks: Array<Function> = [];
    private subscribHeartStatusDataCallbacks: Array<Function> = [];
    private subscribeDrawEarningNotificationDataCallbacks: Array<Function> = [];
    private subscribeLastPriceDataCallbacks: Array<Function> = [];
    private subscribeDrawTradePriceAnnotationsCallbacks: Array<Function> = [];

    private symbol = 'SPY';
    private symbolLabel = '';

    private lastPoint: TIChartPointModel | null = null;
    private pointFromYesterday: TIChartPointModel | null = null;
    private lastPrice = 0;
    private previewSymbolSizePercent = 40;
    private timeframe = Timeframe.FiveMinute;
    private newestDate: Date | null = null;
    private oldestDate: Date | null = null;

    public connection: Connection;
    private chartDataClient: ChartDataClient;
    private topListClient: TopListClient;
    private holidayClient: HolidayClient;

    public waitForLogin = true;
    public useLoginPopup = false;
    public pnpEnabled = true;
    private onReconnectStartRefresh = true;
    private displayVolumeIndicator = true;
    private readonly volumeIndicatorColor = 'red';

    private showPreMarket = true;
    private showPostMarket = true;
    private readonly preMarketMinutes = 330;
    private readonly postMarketMinutes = 300;

    private trace = false;
    private isPnP = false;
    private chartType = ChartType.Candlestick;
    private chartContainer: IChartContainer;

    private chartStyleModel = new ChartStyleSettingsModel();
    private theme = ChartTheme.Light;
    public chartMode = ChartMode.Standard;

    private baseElement: HTMLElement;

    private startingRequestTime: Date | null = null;

    private chartTraceElement: HTMLElement | null = null;

    private mainChartZoomLevel = -5;
    private pipChartZoomLevel = -11;

    constructor(base: IChartContainer, baseElement: HTMLElement) {
        this.chartContainer = base;
        this.baseElement = baseElement;
        this.isPnP = this.chartContainer.pnpMode;

        if (typeof TIJSAPI != 'undefined' && TIJSAPI) {
            console.log(`${this.isPnP ? 'PIP' : ''} Chart is using TIJSAPI`)
            this.connection = TIJSAPI.Connection.getInstance();
            this.chartDataClient = this.connection.chartDataClient;
            this.topListClient = this.connection.topListClient;
            this.holidayClient = this.connection.holidayClient;
        } else {
            this.connection = Connection.getInstance();
            this.chartDataClient = this.connection.chartDataClient;
            this.topListClient = this.connection.topListClient;
            this.holidayClient = this.connection.holidayClient;
        }

        this.setPropertiesFromQueryStringOrDataAttributes();
        this.onResize();
        this.onReconnected();

        this.startingRequestTime = new Date();
        console.log(`web chart ${this.chartContainer.id} subscribeInitChartData`, this.startingRequestTime);

        if (!this.isPnP) {
            this.chartTraceElement = document.querySelector('.' + this.chartContainer.id + '-trace');
            this.chartTraceElement?.findFirst('.start span')?.setInnerHtml(DateHelpers.getTraceLoggingTimeStamp());
        } else {
            this.chartTraceElement = document.querySelector('.' + this.chartContainer.id.replace('-pip', '') + '-trace');
        }
    }

    public getSymbolLabel(): string {
        if (this.symbolLabel) {
            return this.symbolLabel;
        }

        return this.symbol;
    }

    private setPropertiesFromQueryStringOrDataAttributes() {
        const symbol = this.getFromQueryStringOrDataAttribute('symbol');
        if (symbol) {
            this.symbol = symbol.toUpperCase();
        }

        const waitForLoginQuery = this.getFromQueryStringOrDataAttribute('waitforlogin');
        if (waitForLoginQuery) {
            this.waitForLogin = waitForLoginQuery.toLowerCase() === 'true';
        }

        const mainChartZoomLevelQuery = this.getFromQueryStringOrDataAttribute('mainchartzoomlevel');
        const mainChartZoomLevel = parseFloat(mainChartZoomLevelQuery);
        if (!isNaN(mainChartZoomLevel)) {
            this.mainChartZoomLevel = mainChartZoomLevel;
        }

        const pipChartZoomLevelQuery = this.getFromQueryStringOrDataAttribute('pipchartzoomlevel');
        const pipChartZoomLevel = parseFloat(pipChartZoomLevelQuery);
        if (!isNaN(pipChartZoomLevel)) {
            this.pipChartZoomLevel = pipChartZoomLevel;
        }
        
        const onReconnectStartRefreshQuery = this.getFromQueryStringOrDataAttribute('onreconnect-startrefresh');
        if (onReconnectStartRefreshQuery) {
            this.onReconnectStartRefresh = onReconnectStartRefreshQuery.toLowerCase() === 'true';
        }

        const timeframeQuery = this.getFromQueryStringOrDataAttribute('timeframe');
        if (timeframeQuery) {
            const timeframe = TimeFrameToChartTimeFrameMapper.map(timeframeQuery);
            if (timeframe >= 0) {
                this.timeframe = timeframe;
            }
        }

        const useLoginPopupQuery = this.getFromQueryStringOrDataAttribute('useloginpopup');
        if (useLoginPopupQuery) {
            this.setUseLoginPopup(useLoginPopupQuery.toLowerCase() === 'true');
        }

        const traceQuery = this.getFromQueryStringOrDataAttribute('trace');
        if (traceQuery) {
            this.setDebugTrace(traceQuery.toLowerCase() === 'true');
        }

        const symbolLabelQuery = this.getFromQueryStringOrDataAttribute('symbolLabel');
        if (symbolLabelQuery) {
            this.symbolLabel = symbolLabelQuery;
        }

        const pnpDisabledQuery = this.getFromQueryStringOrDataAttribute('disablepip');
        if (pnpDisabledQuery && pnpDisabledQuery.toLowerCase() === 'true') {
            this.pnpEnabled = false;
        }

        const statuslineEnableQuery = this.getFromQueryStringOrDataAttribute('enableStatusLine');
        if (statuslineEnableQuery && statuslineEnableQuery.toLowerCase() === 'true') {
            this.baseElement.findFirst('.tooltip')?.removeClass('hidden');
        }

        const disablescrollingQuery = this.getFromQueryStringOrDataAttribute('disablescrolling');
        if (disablescrollingQuery && disablescrollingQuery.toLowerCase() === 'true') {
            this.chartContainer.disableScrolling = true;
            this.chartContainer.scrollMode = ScrollMode.ClickAndDrag;
        }

        // const scrollModeQuery = this.getFromQueryStringOrDataAttribute('scrollmode');
        // if (scrollModeQuery) {
        //     let scrollMode = ScrollMode.MouseWheel;
        //     if (scrollModeQuery.toLowerCase() === 'clickanddrag') {
        //         scrollMode = ScrollMode.ClickAndDrag;
        //     }

        //     this.chartContainer.scrollMode = scrollMode;
        // }

        if (this.isPnP) {
            this.timeframe = Timeframe.Day;
            this.chartType = ChartType.Area;
            this.displayVolumeIndicator = false;
        } else {
            const displayVolumeIndicatorQuery = this.getFromQueryStringOrDataAttribute('displayvolumeindicator');
            if (displayVolumeIndicatorQuery) {
                this.displayVolumeIndicator = displayVolumeIndicatorQuery.toLowerCase() === 'true';
            }

            const showPreMarketQuery = this.getFromQueryStringOrDataAttribute('showpremarket');
            if (showPreMarketQuery) {
                this.showPreMarket = showPreMarketQuery.toLowerCase() === 'true';
            }

            const showPostMarketQuery = this.getFromQueryStringOrDataAttribute('showpostmarket');
            if (showPostMarketQuery) {
                this.showPostMarket = showPostMarketQuery.toLowerCase() === 'true';
            }

            const previewSymbolSizePercentQuery = this.getFromQueryStringOrDataAttribute('previewsymbolsizepercent');
            if (previewSymbolSizePercentQuery) {
                this.setChartPreviewSymbolSizePercent(parseInt(previewSymbolSizePercentQuery, 10));
            }
        }

        const chartTimeframeStyleQuery = this.getFromQueryStringOrDataAttribute('timeframestyle');
        if (chartTimeframeStyleQuery) {
            if (!this.isPnP) {
                if (chartTimeframeStyleQuery == 'daytrading') {
                    this.timeframe = Timeframe.FiveMinute;
                } else if (chartTimeframeStyleQuery == 'swingtrading') {
                    this.timeframe = Timeframe.Day;
                } else if (chartTimeframeStyleQuery == 'investing') {
                    this.timeframe = Timeframe.Week;
                }
            } else {
                if (chartTimeframeStyleQuery == 'daytrading') {
                    this.timeframe = Timeframe.Day;
                } else if (chartTimeframeStyleQuery == 'swingtrading') {
                    this.timeframe = Timeframe.Week;
                } else if (chartTimeframeStyleQuery == 'investing') {
                    this.timeframe = Timeframe.Month;
                }
            }
        }

        const mainChartTimeframeQuery = this.getFromQueryStringOrDataAttribute('maintimeframe');
        if (mainChartTimeframeQuery && !this.isPnP) {
            const timeframe = TimeFrameToChartTimeFrameMapper.map(timeframeQuery);
            if (timeframe >= 0) {
                this.timeframe = timeframe;
            }
        }

        const pipChartTimeframeQuery = this.getFromQueryStringOrDataAttribute('piptimeframe');
        if (pipChartTimeframeQuery && !this.isPnP) {
            const timeframe = TimeFrameToChartTimeFrameMapper.map(timeframeQuery);
            if (timeframe >= 0) {
                this.timeframe = timeframe;
            }
        }

        const chartThemeQuery = this.getFromQueryStringOrDataAttribute('theme');
        if (chartThemeQuery) {
            this.theme = WebChartThemeHelpers.getTheme(chartThemeQuery.toLowerCase());
        }

        const chartModeQuery = this.getFromQueryStringOrDataAttribute('mode');
        if (chartModeQuery) {
            let chartMode = ChartMode.Standard;
            if (chartModeQuery.toLowerCase() === 'simple') {
                chartMode = ChartMode.Simple;
            } else if (chartModeQuery.toLowerCase() === 'summary') {
                chartMode = ChartMode.Summary;
            } else if (chartModeQuery.toLowerCase() === 'preview') {
                chartMode = ChartMode.Preview;
                this.showPreMarket = false;
                this.showPostMarket = false;
            }

            console.log('chart mode ', chartMode)
            this.chartMode = chartMode;
            this.chartStyleModel = this.getChartStyleSettings(this.theme);
            this.setChartMode(chartMode);
        } else {
            this.chartStyleModel = this.getChartStyleSettings(this.theme);
        }

        const chartUpColorQuery = this.getFromQueryStringOrDataAttribute('upcolor');
        if (chartUpColorQuery) {
            this.setSelectedColor(chartUpColorQuery, true);
        }

        const chartDownColorQuery = this.getFromQueryStringOrDataAttribute('downcolor');
        if (chartDownColorQuery) {
            this.setSelectedColor(chartDownColorQuery, true);
        }

        const mainChartLineAndFillColorQuery = this.getFromQueryStringOrDataAttribute('mainchartlineandfillcolor');
        if (mainChartLineAndFillColorQuery) {
            const colorSplits = mainChartLineAndFillColorQuery.split(',');
            const lineHexColor = colorSplits.length > 0 ? this.getCorrectColorFromString(colorSplits[0]) : '';
            const areaFillHexColor = colorSplits.length > 1 ? this.getCorrectColorFromString(colorSplits[1]) : '';
            const gradientHexColor = colorSplits.length > 2 ? this.getCorrectColorFromString(colorSplits[2]) : '';
            this.setMainChartLineAndFillColor(lineHexColor, areaFillHexColor, gradientHexColor);
        }
    }

    private getFromQueryStringOrDataAttribute(key: string): string {
        // let value = UrlHelpers.getQueryString(key);

        const attrKey = ('data-tichart-' + key).toLowerCase();
        if (this.baseElement && key) {
            for (const attr of Array.from(this.baseElement.attributes)) {
                if (attr.name.toLowerCase() === attrKey) {
                    return attr.value;
                }
            }
        }

        if (this.baseElement?.parentElement) {
            for (const attr of Array.from(this.baseElement.parentElement.attributes)) {
                if (attr.name.toLowerCase() === attrKey) {
                    return attr.value;
                }
            }
        }

        const queryStringValue = UrlHelpers.getQueryString(key);
        if (queryStringValue) {
            return queryStringValue;
        }

        return '';
    }

    public setDebugTrace(on: boolean) {
        this.trace = on;
        window.trace = on;
    }

    public setUseLoginPopup(on: boolean) {
        this.useLoginPopup = on;
    }

    public setSelectedColor(colorString: string, up: boolean) {
        const colorFromString = this.getCorrectColorFromString(colorString);

        if (!colorFromString) {
            return;
        }

        if (up) {
            this.chartStyleModel.selectedUpCandleColor = new ChartStyleColorModel(color.HEX2RGB(colorString));
        } else {
            this.chartStyleModel.selectedDownCandleColor = new ChartStyleColorModel(color.HEX2RGB(colorString));
        }
    }

    private getCorrectColorFromString(colorString: string): string {
        // Hex color pattern: # followed by 3, 4, 6, or 8 hexadecimal characters
        const hexColorRegex = /^#([0-9A-F]{3}|[0-9A-F]{4}|[0-9A-F]{6}|[0-9A-F]{8})$/i;

        if (!colorString || !this.chartStyleModel) {
            return '';
        }

        if (colorString[0] !== '#') {
            colorString = '#' + colorString;
        }

        // Check if the color matches the hex color pattern
        if (!hexColorRegex.test(colorString)) {
            return '';
        }

        return colorString;
    }

    public login(username: string, password: string) {
        // Default dest to null, so the WebSocket Tunnel can route to the correct default destination - 'live' for Prod and 'aws-test' for Dev
        let dest = null;
        const destinationQueryString = UrlHelpers.getQueryString('dest');
        if (destinationQueryString) {
            dest = destinationQueryString;
        }

        this.connection.subcribeToLoginEvent(() => {
            this.onLoggedIn();
        });

        if (!this.connection.isLoggedIn()) {
            this.connection.login(username, password, 3, dest, true);
        }
    }

    public onLoggedIn() {
        if (!this.connection.isLoggedIn()) {
            return;
        }

        if (this.chartContainer.pnpMode) {
            this.onSubscribeInitChartData();
        } else {
            if (this.showPreMarket || this.showPostMarket) {
                this.getHoliday();
            } else {
                this.onSubscribeInitChartData();
            }
        }
    }

    public logout() {
        this.connection.logout();
        this.clearElements();
    }

    private clearElements() {
        const container = this.chartContainer as WebChartsMarketingContainer;
        container.element?.findFirst('.chart-container')?.addClass('hidden');
        container.element?.findFirst('.chart')?.setInnerHtml('');

        container.element?.findFirst('.ti-chart-indicators-style')?.remove();
        container.element?.findFirst('.ti-chart-style')?.remove();
        container.element?.findFirst('.ti-chart-plot-bands-style')?.remove();
        container.element?.findFirst('.ti-chart-grid-lines-style')?.remove();
        container.element?.findFirst('.ti-chart-cross-hairs-style')?.remove();

        container.pnpContainer?.element?.findFirst('.chart-container')?.addClass('hidden');
        container.pnpContainer?.element?.findFirst('.chart')?.setInnerHtml('');

        container.pnpContainer?.element?.findFirst('.ti-chart-indicators-style')?.remove();
        container.pnpContainer?.element?.findFirst('.ti-chart-style')?.remove();
        container.pnpContainer?.element?.findFirst('.ti-chart-plot-bands-style')?.remove();
        container.pnpContainer?.element?.findFirst('.ti-chart-grid-lines-style')?.remove();
        container.pnpContainer?.element?.findFirst('.ti-chart-cross-hairs-style')?.remove();

        const summaryStatusLines = this.chartContainer.getComponentsByType(MarketingWebChartSummaryStatusLineComponent) as Array<MarketingWebChartSummaryStatusLineComponent>;
        if (summaryStatusLines?.length) {
            summaryStatusLines[0].hide();
        }
    }

    private getHoliday() {
        if (!this.isPnP) {
            this.chartTraceElement?.findFirst('.holiday-request span')?.setInnerHtml(DateHelpers.getTraceLoggingTimeStamp());
        }

        const notifyHolidayWatchers = (holidayResponse: Array<HolidayModel>) => {
            if (window.holidayWatchers && window.holidayWatchers.length) {
                for (let i = 0; i < window.holidayWatchers.length; i++) {
                    window.holidayWatchers[i](holidayResponse);
                }
            }
        };

        const addHolidayWatchers = () => {
            if (!window.holidayWatchers) {
                window.holidayWatchers = [];
            }

            window.holidayWatchers.push((holidayResponse: Array<HolidayModel>) => {
                if (typeof TIJSAPI != 'undefined' && TIJSAPI) {
                    TIJSAPI.HolidayManager.setHolidays(holidayResponse);
                } else {
                    HolidayManager.setHolidays(holidayResponse);
                }

                console.log('subscribeInitChartData addHolidayWatchers()')
                if (!this.isPnP) {
                    this.chartTraceElement?.findFirst('.holiday-response span')?.setInnerHtml(DateHelpers.getTraceLoggingTimeStamp());
                }
                this.onSubscribeInitChartData();
            });
        };

        if (!window.loadedHoliday) {
            window.loadedHoliday = true;
            this.holidayClient.getHoliday().then(holidayResponse => {
                if (!holidayResponse.isSuccess) {
                    return;
                }

                if (typeof TIJSAPI != 'undefined' && TIJSAPI) {
                    TIJSAPI.HolidayManager.setHolidays(holidayResponse.data);
                } else {
                    HolidayManager.setHolidays(holidayResponse.data);
                }

                console.log('subscribeInitChartData getHoliday()')
                notifyHolidayWatchers(holidayResponse.data);
                if (!this.isPnP) {
                    this.chartTraceElement?.findFirst('.holiday-response span')?.setInnerHtml(DateHelpers.getTraceLoggingTimeStamp());
                }
                this.onSubscribeInitChartData();
            });
        } else {
            addHolidayWatchers();
        }
    }

    public subscribeChartRequestingNewSymbolData(callback: Function) {
        // this.subscribeChartRequestingNewSymbolDataCallbacks.push(callback);
    }

    public subscribeInitChartData(callback: Function) {
        this.subscribeInitChartDataCallbacks.push(callback);
    }

    private initMarketingXAxisPlotlineInterval: any;
    private chartFinishedRenderInterval: any;
    public onSubscribeInitChartData() {
        if (!this.symbol || (this.isPnP && this.chartMode == ChartMode.Preview)) {
            return;
        }

        this.requestHistoryDataCount = 0;

        const container = this.chartContainer as any;


        const chartDataRequest = new ChartDataRequestModel();
        chartDataRequest.symbols = [this.symbol];

        if (!this.isPnP && this.chartMode == ChartMode.Preview) {
            container.element?.findFirst('.chart-container')?.addClass('hidden');
            container.element?.findFirst('.chart-container')?.addClass('no-opacity');
        }

        if (TimeframeHelper.timeframeIntraday(this.timeframe)) {
            const minutesPerCandle: any = TimeframeHelper.getIntradayChartTimeFrameMinutePerCandle(this.timeframe);
            chartDataRequest.options = { timeFrame: 'intraday', rowCount: chartDataRequest.rowCount, minutesPerCandle: minutesPerCandle };
            if (this.showPreMarket) {
                chartDataRequest.options.startOffset = this.preMarketMinutes;
            }

            if (this.showPostMarket) {
                chartDataRequest.options.endOffset = this.postMarketMinutes;
            }
        } else {
            const candleSize: any = TimeframeHelper.getDailyChartTimeFrameCandleSize(this.timeframe);
            chartDataRequest.options = { timeFrame: 'daily', rowCount: chartDataRequest.rowCount, candleSize: candleSize };
        }

        let indicatorHeaders: Array<string> = [];
        if (this.displayVolumeIndicator) {
            chartDataRequest.gridRequest = [
                ["START", ['current("start")']],
                ["END", ['current("end")']],
                ["OPEN", []],
                ["HIGH", []],
                ["LOW", []],
                ["CLOSE", []],
                ["VOLUME", []]
            ];

            indicatorHeaders = ['volume'];
        }

        if (this.startingRequestTime) {
            const startingGetChartDataTime = new Date();
            console.log(`web chart ${this.chartContainer.id} subscribeInitChartData startingGetChartDataTime time: ${((startingGetChartDataTime.valueOf() - this.startingRequestTime.valueOf()) / 1000)}s`)
        }

        if (!this.isPnP) {
            this.chartTraceElement?.findFirst('.request span')?.setInnerHtml(DateHelpers.getTraceLoggingTimeStamp());
        } else {
            this.chartTraceElement?.findFirst('.pip-request span')?.setInnerHtml(DateHelpers.getTraceLoggingTimeStamp());
        }
        this.chartDataClient.getChartData(chartDataRequest).then((response) => {
            if (!response.isSuccess) {
                return;
            }

            if (!this.isPnP) {
                this.chartTraceElement?.findFirst('.response span')?.setInnerHtml(DateHelpers.getTraceLoggingTimeStamp());
            } else {
                this.chartTraceElement?.findFirst('.pip-response span')?.setInnerHtml(DateHelpers.getTraceLoggingTimeStamp());
            }

            let model = JSApiPointToTiChartDataPointMapper.map(response.data, 0, indicatorHeaders, this.showPreMarket, this.showPostMarket);
            if (model?.length) {
                const lastPoint = model[model.length - 1];
                const firstPoint = model[0];
                this.lastPoint = lastPoint;
                this.lastPrice = lastPoint.close;
                this.oldestDate = firstPoint.start;
                this.newestDate = lastPoint.start;

                if (model.length > 2 && !this.isPnP && this.chartMode == ChartMode.Preview) {
                    this.setPointFromYesterday(model, this.lastPoint);
                    model = this.getPointsFromYesterdayClose(model);

                    console.log(' model getPoints ', model)
                }

                this.updateMarketingSummaryStatusLine();
            }

            if (this.startingRequestTime) {
                const finishedRequestTime = new Date();
                console.log(`web chart ${this.chartContainer.id} subscribeInitChartData total request time: ${((finishedRequestTime.valueOf() - this.startingRequestTime.valueOf()) / 1000)}s`)
            }

            for (const callback of this.subscribeInitChartDataCallbacks) {
                callback(model, this.timeframe, this.symbol, this.chartType);
            }

            const tiIndicatorsData: Array<TIIndicatorDataModel> = [];
            if (this.displayVolumeIndicator) {
                const indicatorDataModel = JSApiPointToTiIndicatorPointMapper.map(model, this.volumeIndicatorColor);
                tiIndicatorsData.push(indicatorDataModel);
            }

            if (!this.isPnP) {
                container.element?.findFirst('.chart-container')?.removeClass('hidden');
            }

            // this.onSubscribeTICompanyLogoData();
            this.onSubscribeChartStyleData();
            this.onSubscribeInitChartIndicatorsData(tiIndicatorsData);
            this.onSubscribeLastPriceData();
            this.subscribeTopListData();
            this.set10sRefreshTimer();

            if (!this.isPnP && this.chartMode == ChartMode.Preview) {
                clearInterval(this.initMarketingXAxisPlotlineInterval);
                this.initMarketingXAxisPlotlineInterval = setInterval(() => {
                    const yAxis = this.chartContainer.tiChartSeries?.highStockChart?.yAxis;
                    if (yAxis?.length) {
                        clearInterval(this.initMarketingXAxisPlotlineInterval);
                        this.setPreviewModeXAxisPointPlotLine();

                        if (this.startingRequestTime) {
                            const finishedRequestTime = new Date();
                            console.log(`web chart ${this.chartContainer.id} subscribeInitChartData finished render time: ${((finishedRequestTime.valueOf() - this.startingRequestTime.valueOf()) / 1000)}s`)
                        }
                    }
                }, 50);

                setTimeout(() => {
                    container.element?.findFirst('.chart-container')?.removeClass('no-opacity');
                }, 100);
            }

            if (!this.isPnP) {
                clearInterval(this.chartFinishedRenderInterval);
                this.chartFinishedRenderInterval = setInterval(() => {
                    const container = this.chartContainer as WebChartsMarketingContainer;
                    const yAxis = this.chartContainer.tiChartSeries?.highStockChart?.yAxis;
                    if (container.pnpContainer && this.chartMode == ChartMode.Standard) {
                        const pipYAxis = container.pnpContainer.tiChartSeries?.highStockChart?.yAxis;
                        if (yAxis?.length && pipYAxis?.length) {
                            clearInterval(this.chartFinishedRenderInterval);
                            this.chartTraceElement?.findFirst('.finished span')?.setInnerHtml(DateHelpers.getTraceLoggingTimeStamp());
                        }
                    } else if (yAxis?.length) {
                        clearInterval(this.chartFinishedRenderInterval);
                        this.chartTraceElement?.findFirst('.finished span')?.setInnerHtml(DateHelpers.getTraceLoggingTimeStamp());
                    }
                }, 10);
            }
        });
    }

    private setPreviewModeXAxisPointPlotLine(setHeightOnly?: boolean) {
        if (this.chartMode == ChartMode.Preview && !this.isPnP) {
            this.xAxisPlotLine?.remove();
            this.xAxisPlotLine = null;
            const color = this.marketingSummaryColor.toHex();

            let setYAxisHeightOnly = false;
            if (setHeightOnly) {
                setYAxisHeightOnly = true;
            }

            this.xAxisPlotLine = new MarketingWebChartXAxisPlotLineComponent(
                this.chartContainer,
                this.pointFromYesterday?.close ?? 0,
                color,
                setYAxisHeightOnly,
                this.isMarketingSummaryUp);
        }
    }

    private xAxisPlotLine: MarketingWebChartXAxisPlotLineComponent | null = null;
    private xAxisPlotLinePoint: TIChartPointModel | null = null;
    private highestHighPoint: TIChartPointModel | null = null;
    public getPointsFromYesterdayClose(model: Array<TIChartPointModel>): Array<TIChartPointModel> {
        if (this.pointFromYesterday && this.chartMode == ChartMode.Preview) {
            let newXValue = 0;
            let newPointsToAdd: Array<TIChartPointModel> = [];
            this.xAxisPlotLinePoint = null;

            for (const point of model) {
                if (point.xValue > this.pointFromYesterday.xValue) {
                    point.xValue = newXValue;
                    newPointsToAdd.push(point);

                    newXValue++;

                    if (!this.xAxisPlotLinePoint) {
                        this.xAxisPlotLinePoint = point;
                    }
                }

                if (!this.highestHighPoint || this.highestHighPoint.high < point.high) {
                    this.highestHighPoint = point;
                }
            }

            console.log('this.xAxisPlotLinePoint ', this.xAxisPlotLinePoint)
            model = newPointsToAdd;
        }

        return model;
    }

    private setPointFromYesterday(points: Array<TIChartPointModel>, lastPoint: TIChartPointModel) {
        const nowInMarketTimeZone = DateHelpers.toMarketTimeZone(lastPoint.start);
        const midnight = nowInMarketTimeZone.set({ hour: 0, minute: 0, second: 0, millisecond: 0 });
        for (let i = points.length - 1; i >= 0; i--) {
            const startTime = DateHelpers.toMarketTimeZone(points[i].start);
            if (startTime < midnight) {
                this.pointFromYesterday = points[i];
                break;
            }
        }

        console.log('chart id ', this.baseElement.id, 'this.pointFromYesterday ', this.pointFromYesterday)
    }

    private timer10sRefresh: any;
    private set10sRefreshTimer() {
        clearTimeout(this.timer10sRefresh);
        this.timer10sRefresh = setTimeout(() => {
            this.onSubscribeUpdate10sRefreshChartData();
        }, 10000)
    }

    private onReconnected() {
        if (this.onReconnectStartRefresh) {
            console.log(`web chart ${this.chartContainer.id} will start refresh when reconnect`);
        }

        this.connection.subscribeOnReconnected(() => {
            console.log(DateHelpers.getTraceLoggingTimeStamp(), ' Chart Reconnected ...');
            if (this.onReconnectStartRefresh) {
                this.set10sRefreshTimer();
            }
        });
    }

    private subscribeTopListData() {
        if (!this.symbol) {
            return;
        }

        const topListRequest = new TopListDataRequestModel();
        topListRequest.resultCount = 1;

        topListRequest.singleSymbol = this.symbol;
        topListRequest.streaming = true;
        topListRequest.outsideMarketHours = true;
        topListRequest.skipMetaData = false;
        topListRequest.addExtraColumn('Price');
        topListRequest.addExtraColumn('EarningD');
        this.topListClient.subscribe(topListRequest, (response: ClientResponseModel<TopListDataResponseModel>) => {
            if (!response.isSuccess || !response.data) {
                return;
            }

            const data = response.data;
            console.log(response);

            let price = 0;
            let businessDays = 0;
            for (let i = 0; i < data.rows.length; i++) {
                const row = data.rows[i];
                price = parseFloat(this.getRowValue(row, '[Price]'));
                businessDays = Math.trunc(parseFloat(this.getRowValue(row, '[EarningD]')));
            }

            this.lastPrice = price;

            // console.log('price ' , price)
            // console.log('business day ', businessDays)

            this.updateMarketingSummaryStatusLine();
            this.onSubscribeLastPriceData();
            this.onSubscribeDrawEarningNotificationData(businessDays);
        },
            (metaData: any) => {
                console.log("toplist metadata response", metaData);
            });
    }

    // private count = 0;
    private marketingSummaryColor = new ChartStyleColorModel();
    private isMarketingSummaryUp = false;
    private updateMarketingSummaryStatusLine() {
        const summaryStatusLines = this.chartContainer.getComponentsByType(MarketingWebChartSummaryStatusLineComponent) as Array<MarketingWebChartSummaryStatusLineComponent>;
        if (!summaryStatusLines?.length) {
            return;
        }

        if (this.chartMode != ChartMode.Summary && this.chartMode != ChartMode.Preview) {
            summaryStatusLines[0].hide();
            return;
        }

        if (!this.lastPoint || !summaryStatusLines?.length) {
            return;
        }

        this.isMarketingSummaryUp = false;

        // console.log('updateMarketingSummaryStatusLine ');
        summaryStatusLines[0].scaleElementPreviewMode(this.previewSymbolSizePercent);
        summaryStatusLines[0].setValue(this.lastPrice, this.pointFromYesterday?.close ?? 0);
        const changesFromClose = summaryStatusLines[0].getChangesFromClose();
        // if (changesFromClose >= 0 && this.count <= 5) {
        if (changesFromClose >= 0) {
            this.isMarketingSummaryUp = true;
            this.marketingSummaryColor = this.chartStyleModel.selectedUpCandleColor;
            const colorHex = this.marketingSummaryColor.toHex();
            this.setMainChartLineAndFillColor(colorHex, colorHex, colorHex);
        } else {
            this.marketingSummaryColor = this.chartStyleModel.selectedDownCandleColor;
            const colorHex = this.marketingSummaryColor.toHex();
            this.setMainChartLineAndFillColor(colorHex, colorHex, colorHex);
        }

        this.onSubscribeChartStyleData();

        this.renderDebugLabel();
        summaryStatusLines[0].render(this.marketingSummaryColor, this.isMarketingSummaryUp, this.getSymbolLabel());
        summaryStatusLines[0].show();
    }

    public renderDebugLabel() {
        if (!this.trace) {
            return;
        }

        const highestHigh = FormatHelpers.numberToString(this.highestHighPoint?.high ?? 0, 2)
        const yesterdayClose = FormatHelpers.numberToString(this.pointFromYesterday?.close ?? 0, 2)
        const todayClose = FormatHelpers.numberToString(this.xAxisPlotLinePoint?.close ?? 0, 2)
        const todayOpen = FormatHelpers.numberToString(this.xAxisPlotLinePoint?.open ?? 0, 2)
        this.chartContainer.tiChartSeries.element.findFirst('.debug-label')?.removeClass('hidden');
        this.chartContainer.tiChartSeries.element.findFirst('.debug-label')?.setInnerHtml(`
            <p>Yesterday Close: ${yesterdayClose}</p>
            <p>Today Close: ${todayClose}</p>
            <p>Today Open ${todayOpen}</p>
            <p>Today Highest Hight ${highestHigh}</p>`)
    }

    private resizeTimer: any;
    private clientWidth = document.body.clientWidth;
    private clientHeight = document.body.clientHeight;
    private onResize() {
        this.chartContainer.domEvents.onResize(() => {
            clearTimeout(this.resizeTimer);
            this.resizeTimer = setTimeout(() => {
                if (this.clientWidth != document.body.clientWidth || this.clientHeight != document.body.clientHeight) {
                    this.clientWidth = document.body.clientWidth;
                    this.clientHeight = document.body.clientHeight;
                    this.adjustPreviewModeContainer();
                }
            }, 50);
        });
    }

    private adjustPreviewModeContainer() {
        if (this.chartMode != ChartMode.Preview) {
            return;
        }

        this.updateMarketingSummaryStatusLine();

        if (this.chartContainer.tiChartSeries?.highStockChart) {
            const yAxis = this.chartContainer.tiChartSeries?.highStockChart?.yAxis[0];
            yAxis?.update({
                height: null
            }, true);

            this.chartContainer.tiChartSeries?.highStockChart.update({
                chart: {
                    height: null
                }
            });

            this.chartContainer.tiChartSeries?.highStockChart.update({
                chart: {
                    height: this.chartContainer.tiChartSeries.element.clientHeight
                }
            });

            this.chartContainer.tiChartSeries?.highStockChart.reflow();

            setTimeout(() => {
                this.setPreviewModeXAxisPointPlotLine(true);
                setTimeout(() => {
                    this.setPreviewModeXAxisPointPlotLine();
                }, 25);
            }, 50);
        }
    }

    private getRowValue(row: Map<string, string>, columnName: string) {
        if (row.get(columnName))
            return row.get(columnName);
        else
            return '';
    }

    public subscribeInitChartIndicatorsData(callback: Function) {
        this.subscribeInitChartIndicatorsDataCallbacks.push(callback);
    }

    private onSubscribeInitChartIndicatorsData(model: Array<TIIndicatorDataModel>) {
        for (const callback of this.subscribeInitChartIndicatorsDataCallbacks) {
            callback(model);
        }
    }

    public subscribeInitChartComparisonsData(callback: Function) {
        this.subscribeInitChartComparisonsDataCallbacks.push(callback);
    }

    public subscribeInitChartDrawingTools(callback: Function) {
        this.subscribeInitChartDrawingToolsCallbacks.push(callback);
    }

    public subscribeUpdate10sRefreshChartData(callback: Function) {
        this.subscribeUpdate10sRefreshChartDataCallbacks.push(callback);
    }

    private onSubscribeUpdate10sRefreshChartData() {
        if (!this.symbol) {
            return;
        }

        const chartDataRequest = new ChartDataRequestModel();
        chartDataRequest.symbols = [this.symbol];

        chartDataRequest.rowCount = 2;
        chartDataRequest.extraRowCount = 1;
        chartDataRequest.sinceDate = this.newestDate;
        
        if (TimeframeHelper.timeframeIntraday(this.timeframe)) {
            const minutesPerCandle: any = TimeframeHelper.getIntradayChartTimeFrameMinutePerCandle(this.timeframe);
            chartDataRequest.options = { timeFrame: 'intraday', rowCount: 2, minutesPerCandle: minutesPerCandle, atTime: this.newestDate };
            if (this.showPreMarket) {
                chartDataRequest.options.startOffset = this.preMarketMinutes;
            }

            if (this.showPostMarket) {
                chartDataRequest.options.endOffset = this.postMarketMinutes;
            }
        } else {
            const candleSize: any = TimeframeHelper.getDailyChartTimeFrameCandleSize(this.timeframe);
            chartDataRequest.options = { timeFrame: 'daily', rowCount: 2, candleSize: candleSize, atTime: this.newestDate };
        }

        let indicatorHeaders: Array<string> = [];
        if (this.displayVolumeIndicator) {
            chartDataRequest.gridRequest = [
                ["START", ['current("start")']],
                ["END", ['current("end")']],
                ["OPEN", []],
                ["HIGH", []],
                ["LOW", []],
                ["CLOSE", []],
                ["VOLUME", []]
            ];

            indicatorHeaders = ['volume'];
        }

        console.log('onSubscribeUpdate10sRefreshChartData ', chartDataRequest)

        const staringRequestTime = new Date();
        console.log(`web chart ${this.chartContainer.id} subscribeUpdate10sRefreshChartData`, staringRequestTime);

        this.chartDataClient.getChartData(chartDataRequest).then(response => {
            if (!response.isSuccess) {
                return;
            }

            let model = JSApiPointToUpdateTiChartDataPointMapper.map(response.data, this.lastPoint, indicatorHeaders, this.showPreMarket, this.showPostMarket);

            if (model?.length) {
                this.lastPoint = model[model.length - 1];
                this.lastPrice = this.lastPoint.close;
                this.newestDate = this.lastPoint.start;
                
                if (model.length > 2 && !this.isPnP && this.chartMode == ChartMode.Preview) {
                    this.setPointFromYesterday(model, this.lastPoint);
                    model = this.getPointsFromYesterdayClose(model);
                }

                this.updateMarketingSummaryStatusLine();
            }

            const finishedRequestTime = new Date();

            console.log(`web chart ${this.chartContainer.id} subscribeUpdate10sRefreshChartData response: `, model)
            console.log(`web chart ${this.chartContainer.id} subscribeUpdate10sRefreshChartData total request time: ${((finishedRequestTime.valueOf() - staringRequestTime.valueOf()) / 1000)}s`)
            for (const callback of this.subscribeUpdate10sRefreshChartDataCallbacks) {
                callback(model);
            }

            const tiIndicatorsData: Array<TIIndicatorDataModel> = [];
            if (this.displayVolumeIndicator) {
                const indicatorDataModel = JSApiPointToTiIndicatorPointMapper.map(model, this.volumeIndicatorColor);
                tiIndicatorsData.push(indicatorDataModel);
            }

            this.onSubscribeLastPriceData();
            this.onSubscribeUpdate10sRefreshIndicatorsData(tiIndicatorsData);
            this.set10sRefreshTimer();

            if (!this.isPnP && this.chartMode == ChartMode.Preview) {
                setTimeout(() => {
                    this.setPreviewModeXAxisPointPlotLine();
                    // if (this.onPreviewChartInitWith10sRefreshCallback) {
                    //     this.onPreviewChartInitWith10sRefreshCallback();
                    //     this.onPreviewChartInitWith10sRefreshCallback = null;
                    // }
                }, 50);
            }
        });
    }

    public subscribeUpdate10sRefreshIndicatorsData(callback: Function) {
        this.subscribeUpdate10sRefreshIndicatorsDataCallbacks.push(callback);
    }

    private onSubscribeUpdate10sRefreshIndicatorsData(model: Array<TIIndicatorDataModel>) {
        for (const callback of this.subscribeUpdate10sRefreshIndicatorsDataCallbacks) {
            callback(model);
        }
    }

    public subscribeUpdateChartHistoryData(callback: Function) {
        this.subscribeUpdateChartHistoryDataCallbacks.push(callback)
    }

    private requestHistoryDataCount = 0;
    private onSubscribeUpdateChartHistoryData() {
        const model: Array<TIChartPointModel> = [];
        if (!this.symbol || !this.oldestDate) {
            for (const callback of this.subscribeUpdateChartHistoryDataCallbacks) {
                callback(model);
            }
            return;
        }

        const chartDataRequest = new ChartDataRequestModel();
        chartDataRequest.symbols = [this.symbol];
        chartDataRequest.startBefore = this.oldestDate;

        if (TimeframeHelper.timeframeIntraday(this.timeframe)) {
            const minutesPerCandle: any = TimeframeHelper.getIntradayChartTimeFrameMinutePerCandle(this.timeframe);
            chartDataRequest.options = {
                timeFrame: 'intraday',
                rowCount: chartDataRequest.rowCount,
                minutesPerCandle: minutesPerCandle,
                atTime: this.oldestDate
            };

            if (this.showPreMarket) {
                chartDataRequest.options.startOffset = this.preMarketMinutes;
            }

            if (this.showPostMarket) {
                chartDataRequest.options.endOffset = this.postMarketMinutes;
            }
        } else {
            const candleSize: any = TimeframeHelper.getDailyChartTimeFrameCandleSize(this.timeframe);
            chartDataRequest.options = {
                timeFrame: 'daily',
                rowCount: chartDataRequest.rowCount,
                candleSize: candleSize,
                atTime: this.oldestDate
            };
        }

        let indicatorHeaders: Array<string> = [];
        if (this.displayVolumeIndicator) {
            chartDataRequest.gridRequest = [
                ["START", ['current("start")']],
                ["END", ['current("end")']],
                ["OPEN", []],
                ["HIGH", []],
                ["LOW", []],
                ["CLOSE", []],
                ["VOLUME", []]
            ];

            indicatorHeaders = ['volume'];
        }

        this.chartDataClient.getChartData(chartDataRequest).then(response => {
            if (!response.isSuccess) {
                return;
            }

            this.requestHistoryDataCount++;
            let items = response.data.get(this.symbol);
            const model = JSApiPointToTiChartDataPointMapper.map(response.data, (((items.grid?.length ?? 0) - 2) * this.requestHistoryDataCount) * -1, indicatorHeaders, this.showPreMarket, this.showPostMarket);

            if (model?.length) {
                const firstPoint = model[0];
                this.oldestDate = firstPoint.start;
            }

            for (const callback of this.subscribeUpdateChartHistoryDataCallbacks) {
                callback(model);
            }

            const tiIndicatorsData: Array<TIIndicatorDataModel> = [];
            if (this.displayVolumeIndicator) {
                const indicatorDataModel = JSApiPointToTiIndicatorPointMapper.map(model, this.volumeIndicatorColor);
                tiIndicatorsData.push(indicatorDataModel);
            }

            this.onSubscribeUpdateHistoryIndicatorsData(tiIndicatorsData);
        });
    }

    public subscribeUpdateHistoryIndicatorsData(callback: Function) {
        this.subscribeUpdateHistoryIndicatorsDataCallbacks.push(callback);
    }

    private onSubscribeUpdateHistoryIndicatorsData(model: Array<TIIndicatorDataModel>) {
        for (const callback of this.subscribeUpdateHistoryIndicatorsDataCallbacks) {
            callback(model);
        }
    }

    public subscribeUpdateChartHistoryComparisonData(callback: Function) {
        this.subscribeUpdateChartHistoryComparisonDataCallbacks.push(callback);
    }

    public subscribeRealtimeData(callback: Function) {
        this.subscribeRealtimeDataCallbacks.push(callback);
    }

    public setMainChartLineAndFillColor(lineHexColor: string, areaFillHexColor: string, gradientHexColor: string) {
        if (!this.isPnP && this.chartMode != ChartMode.Standard) {
            console.log('set setMainChartLineAndFillColor ', lineHexColor, areaFillHexColor, gradientHexColor)
            if (lineHexColor) {
                this.chartStyleModel.selectedLineColor = new ChartStyleColorModel(color.HEX2RGB(lineHexColor));
            }
            if (areaFillHexColor) {
                this.chartStyleModel.marketingChartAreaFillHexColor = new ChartStyleColorModel(color.HEX2RGB(areaFillHexColor));
            }
            if (gradientHexColor) {
                const gradientColor = new ChartStyleColorModel(color.HEX2RGB(gradientHexColor));
                let gradientHex = color.HEX2RGB(gradientColor.lighten(65));
                if (this.theme == ChartTheme.Dark) {
                    gradientHex = color.HEX2RGB(gradientColor.darken(65));
                }
                this.chartStyleModel.marketingChartGradientHexColor = new ChartStyleColorModel(gradientHex);
            }
        }
    }

    public setChartMode(chartMode: ChartMode) {
        this.chartMode = chartMode;
        if (this.chartMode == ChartMode.Preview) {
            this.chartContainer.toggleChartToPreviewMode(true);
        } else {
            this.chartContainer.toggleChartToPreviewMode(false);
        }

        this.chartStyleModel.pnpEnabled = this.chartMode == ChartMode.Standard;
        const summaryStatusLines = this.chartContainer.getComponentsByType(MarketingWebChartSummaryStatusLineComponent) as Array<MarketingWebChartSummaryStatusLineComponent>;
        if (!this.isPnP) {
            if (this.chartMode != ChartMode.Standard) {
                this.showPreMarket = false;
                this.showPostMarket = false;
                this.displayVolumeIndicator = false;
                this.chartType = ChartType.Area;

                if (this.chartContainer.isPreview) {
                    const isAfterNoon = DateHelpers.isNowMarketTimeZoneAfterNoon();
                    const isAfterTwoPM = DateHelpers.isNowMarketTimeZoneAfter2PM();
                    this.timeframe = Timeframe.OneMinute;
                    if (isAfterTwoPM) {
                        this.timeframe = Timeframe.FiveMinute;
                    } else if (isAfterNoon) {
                        this.timeframe = Timeframe.TwoMinute;
                    }

                    console.log('time frame ', this.timeframe)
                } else {
                    this.timeframe = Timeframe.Day;
                }

                this.chartStyleModel.chartType = this.chartType;
                this.chartStyleModel.isOneColor = true;
                this.chartStyleModel.selectedLineColor = new ChartStyleColorModel({ r: 90, g: 185, b: 116 });
                this.chartStyleModel.isGradientAreaColorChecked = true;

                const lineColor1 = this.chartStyleModel.selectedLineColor.lighten(50);
                const lineColor2 = new ChartStyleColorModel(color.HEX2RGB(lineColor1));

                this.chartStyleModel.marketingChartAreaFillHexColor = this.chartStyleModel.selectedLineColor;
                this.chartStyleModel.marketingChartGradientHexColor = lineColor2;

                if (this.chartStyleModel.isDarkTheme) {
                    this.chartStyleModel.selectedChartGradientColor = this.chartStyleModel.selectedChartBackgroundColor;
                } else {
                    this.chartStyleModel.selectedChartBackgroundColor = new ChartStyleColorModel({ r: 255, g: 255, b: 255 });
                    this.chartStyleModel.selectedChartGradientColor = new ChartStyleColorModel({ r: 255, g: 255, b: 255 });
                }

                if ((this.chartMode == ChartMode.Summary || this.chartMode == ChartMode.Preview) && summaryStatusLines?.length) {
                    summaryStatusLines[0].show();
                }
            } else {
                this.chartType = ChartType.Candlestick;
                this.chartStyleModel = this.getChartStyleSettings(this.theme);

                const timeframeQuery = this.getFromQueryStringOrDataAttribute('timeframe');
                if (timeframeQuery) {
                    const timeframe = TimeFrameToChartTimeFrameMapper.map(timeframeQuery);
                    if (timeframe >= 0) {
                        this.timeframe = timeframe;
                    }
                }

                const displayVolumeIndicatorQuery = this.getFromQueryStringOrDataAttribute('displayvolumeindicator');
                if (displayVolumeIndicatorQuery) {
                    this.displayVolumeIndicator = displayVolumeIndicatorQuery.toLowerCase() === 'true';
                }

                const showPreMarketQuery = this.getFromQueryStringOrDataAttribute('showpremarket');
                if (showPreMarketQuery) {
                    this.showPreMarket = showPreMarketQuery.toLowerCase() === 'true';
                }

                const showPostMarketQuery = this.getFromQueryStringOrDataAttribute('showpostmarket');
                if (showPostMarketQuery) {
                    this.showPostMarket = showPostMarketQuery.toLowerCase() === 'true';
                }

                if (summaryStatusLines?.length) {
                    summaryStatusLines[0].hide();
                }
            }
        }
    }

    public setChartPreviewSymbolSizePercent(percentage: number) {
        this.previewSymbolSizePercent = percentage;
    }

    public subscribeChartStyleData(callback: Function) {
        this.subscribeChartStyleDataCallbacks.push(callback);
    }

    public setChartStyleData(theme: ChartTheme) {
        this.theme = theme;
        this.chartStyleModel = this.getChartStyleSettings(this.theme);
        this.setChartMode(this.chartMode);
    }

    private getChartStyleSettings(theme: ChartTheme): ChartStyleSettingsModel {
        const themeModel = WebchartThemeManager.getInstance().getChartThemeByName(theme, this.isPnP, this.chartType, this.showPostMarket, this.showPreMarket, this.pnpEnabled, this.chartMode);
        if (!themeModel) {
            return new ChartStyleSettingsModel();
        }

        if (this.chartMode == ChartMode.Preview) {
            themeModel.selectedUpCandleColor = new ChartStyleColorModel({
                r: 16,
                g: 124,
                b: 16
            });

            themeModel.selectedDownCandleColor = new ChartStyleColorModel({
                r: 209,
                g: 52,
                b: 56
            });

            if (this.theme == ChartTheme.Dark) {
                themeModel.selectedUpCandleColor = new ChartStyleColorModel({
                    r: 89,
                    g: 186,
                    b: 109
                });

                themeModel.selectedDownCandleColor = new ChartStyleColorModel({
                    r: 210,
                    g: 58,
                    b: 45
                });
            }

            themeModel.chartTypeLineWidth = 4;
            themeModel.zoomLevel = 0;

            if (this.chartContainer.element.clientWidth <= 350) {
                themeModel.chartTypeLineWidth = 2;
            }

            if (this.chartContainer.element.clientWidth <= 125) {
                themeModel.chartTypeLineWidth = 1;
            }
        } else {
            themeModel.zoomLevel = this.mainChartZoomLevel;
        }

        const lineColor1 = themeModel.selectedLineColor.lighten(50);
        const lineColor2 = new ChartStyleColorModel(color.HEX2RGB(lineColor1));
        themeModel.marketingChartAreaFillHexColor = themeModel.selectedLineColor;
        themeModel.marketingChartGradientHexColor = lineColor2;

        if (this.isPnP) {
            themeModel.zoomLevel = this.pipChartZoomLevel;
        }
        
        return themeModel;
    }

    public onSubscribeChartStyleData() {
        for (const callback of this.subscribeChartStyleDataCallbacks) {
            callback(new ChartStyleSettingsModel(ObjectHelpers.copy(this.chartStyleModel)));
        }
    }

    public subscribePnlData(callback: Function) {
        this.subscribePnlDataCallbacks.push(callback);
    }

    public subscribeCompanyLogoData(callback: Function) {
        this.subscribeCompanyLogoDataCallbacks.push(callback);
    }

    public subscribeCurrentPositionData(callback: Function) {
        this.subscribeCurrentPositionDataCallbacks.push(callback);
    }

    public subscribeTICompanyLogoData(callback: Function) {
        // this.subscribeTICompanyLogoDataCallbacks.push(callback);
    }

    public subscribeChartRequirementsData(callback: Function) {
        // console.log('subscribeChartRequirementsData')
        this.subscribeChartRequirementsDataCallbacks.push(callback);
    }

    public subscribeChartAllowBuyData(callback: Function) {
        this.subscribeChartAllowBuyDataCallbacks.push(callback);
    }

    public subscribeChartAllowSellData(callback: Function) {
        this.subscribeChartAllowSellDataCallbacks.push(callback);
    }

    public subscribeDrawTradeOrdersData(callback: Function) {
        this.subscribeDrawTradeOrdersDataCallbacks.push(callback);
    }

    public subscribeDrawOrderExecutionData(callback: Function) {
        this.subscribeDrawOrderExecutionDataCallbacks.push(callback);
    }

    public subscribePriceAlertsData(callback: Function) {
        this.subscribePriceAlertsDataCallbacks.push(callback);
    }

    public subscribePriceAlertsImageData(callback: Function) {
        this.subscribePriceAlertsImageDataCallbacks.push(callback);
    }

    public subscribeEarningNotificationData(callback: Function) {
        this.subscribeEarningNotificationDataCallbacks.push(callback);
    }

    public subscribeHaltedNotificationData(callback: Function) {
        this.subscribeHaltedNotificationDataCallbacks.push(callback);
    }

    public subscribeLastPriceData(callback: Function) {
        this.subscribeLastPriceDataCallbacks.push(callback);
    }

    private onSubscribeLastPriceData() {
        for (const callback of this.subscribeLastPriceDataCallbacks) {
            callback(this.lastPrice);
        }
    }

    public subscribeDrawTradePriceAnnotations(callback: Function) {
        this.subscribeDrawTradePriceAnnotationsCallbacks.push(callback);
    }

    public subscribeWindowMinimizeUpdate(callback: Function) {
        // this.subscribeWindowMinimizeUpdateCallbacks.push(callback);
    }

    private onSubscribeWindowMinimizeUpdate() {
        // window.redraw = () => {
        //     console.log('onSubscribeRedraw ')
        //     for (const callback of this.subscribeWindowMinimizeUpdateCallbacks) {
        //         callback();
        //     }
        // };
    }

    public subscribeSwingExitData(callback: Function) {
        this.subscribeSwingExitDataCallbacks.push(callback);
    }

    public subscribeSmartRiskData(callback: Function) {
        this.subscribSmartRiskDataCallbacks.push(callback);
    }

    public subscribeHeartStatusData(callback: Function) {
        this.subscribHeartStatusDataCallbacks.push(callback);
    }

    public subscribeDrawEarningNotificationData(callback: Function) {
        this.subscribeDrawEarningNotificationDataCallbacks.push(callback);
    }

    private onSubscribeDrawEarningNotificationData(businessDays: number) {
        console.log('businessDays ', businessDays)
        console.log('Number.isNaN(businessDays) ', Number.isNaN(businessDays))
        if (Number.isNaN(businessDays) || businessDays >= 0 || this.chartMode != ChartMode.Standard) {
            return;
        }

        const pastEarningsDate = DateHelpers.addBusinessDay(new Date(), parseInt(Math.trunc(businessDays - 1) + ''));
        const pastEarningsDateDecimal = parseInt(((businessDays - parseInt(businessDays + '')) * 100) + '');

        const now = new Date();
        console.log('now ', now)
        console.log('pastEarningsDate ', pastEarningsDate)
        console.log('pastEarningsDateDecimal ', pastEarningsDateDecimal)

        if (Number.isNaN(pastEarningsDateDecimal) || !this.chartContainer.tiChartSeries.tiChartPoints.length) {
            return;
        }

        let model: DrawEarningNotificationDataModel = null;
        for (const dataPoint of this.chartContainer.tiChartSeries.tiChartPoints) {

            const pointStartDateStr = DateHelpers.format('dd-mm-yyyy', dataPoint.start);
            const pastEarningsDateStr = DateHelpers.format('dd-mm-yyyy', pastEarningsDate);
            if (pointStartDateStr == pastEarningsDateStr) {
                // var marketStatus = "During Market";
                // if (pastEarningsDateDecimal == -25) {
                //     marketStatus = "Post-Market";
                // }
                // else if (pastEarningsDateDecimal == -75) {
                //     marketStatus = "Pre-Market";
                // }

                // let tooltip = `Earnings - ${pastEarningsDateStr} ${marketStatus} Hours.`;
                let xValue = dataPoint.xValue;
                model = new DrawEarningNotificationDataModel({
                    pastEarningsDateDecimal: pastEarningsDateDecimal,
                    earningDate: pastEarningsDate,
                    xValue: xValue
                });

                break;
            }
        }

        if (model) {
            for (const callback of this.subscribeDrawEarningNotificationDataCallbacks) {
                callback(model);
            }
        }
    }

    public subscribeEmojiData(callback: Function) {
    }

    public notifyChartHideContextMenu() {
        // if (!this.winformObjHasValue()) {
        //     return;
        // }

        // winformObj.hideContextMenu();
    }

    public requestHistory() {
        this.onSubscribeUpdateChartHistoryData();
    }

    public requestOpenSymbolLookup(symbol: string) {
        // if (!this.winformObjHasValue()) {
        //     return;
        // }

        // winformObj.openSymbolLookup(symbol);
    }

    public requestNewSymbol(symbol: string) {
        this.symbol = symbol;

        this.onSubscribeInitChartData();
    }

    public requestNewTimeframe(timeframe: Timeframe) {
        this.timeframe = timeframe;

        this.onSubscribeInitChartData();
    }

    public requestNewChartType(chartType: ChartType) {
        // if (!this.winformObjHasValue()) {
        //     return;
        // }

        // winformObj.switchChartType(chartType);
    }

    public requestBuy() {
        // if (!this.winformObjHasValue()) {
        //     return;
        // }

        // winformObj.selectBuy();
    }

    public requestSell() {
        // if (!this.winformObjHasValue()) {
        //     return;
        // }

        // winformObj.selectSell();
    }

    public requestInitiateChartTrade(price: number, isLong: boolean) {
        // if (!this.winformObjHasValue()) {
        //     return;
        // }

        // winformObj.requestInitiateChartTrade(price, isLong);
    }

    public requestUpdateChartTrade(orderId: string, price: number) {
        // if (!this.winformObjHasValue()) {
        //     return;
        // }

        // winformObj.requestUpdateChartTrade(orderId, price);
    }

    public requestUpdateLimitAlert(id: string, price: number) {
        // if (!this.winformObjHasValue()) {
        //     return;
        // }

        // winformObj.requestUpdateLimitAlert(id, price);
    }

    public requestRemoveChartTrade(orderId: string) {
        // if (!this.winformObjHasValue()) {
        //     return;
        // }

        // winformObj.requestRemoveChartTrade(orderId);
    }

    public requestRemoveLimitAlert(id: string) {
        // if (!this.winformObjHasValue()) {
        //     return;
        // }

        // winformObj.requestRemoveLimitAlert(id);
    }

    public requestUpdateLastPriceClicked(price: number) {
        // if (!this.winformObjHasValue()) {
        //     return;
        // }

        // winformObj.requestUpdateLastPriceClicked(price);
    }

    public requestAddIndicators() {
        // if (!this.winformObjHasValue()) {
        //     return;
        // }

        // winformObj.requestAddIndicators();
    }

    public requestAddPriceAlert() {
        // if (!this.winformObjHasValue()) {
        //     return;
        // }

        // winformObj.requestAddPriceAlert();
    }

    public requestAddAVWap(xValue: number) {
        // if (!this.winformObjHasValue()) {
        //     return;
        // }

        // winformObj.requestAddAVWap(xValue);
    }

    public requestAddComparisonSymbols() {
        // if (!this.winformObjHasValue()) {
        //     return;
        // }

        // winformObj.requestAddComparisonSymbols();
    }

    public requestOpenComparisonSymbolLookup(symbol: string) {
        // if (!this.winformObjHasValue()) {
        //     return;
        // }

        // winformObj.requestOpenComparisonSymbolLookup(symbol);
    }

    public requestSetHeartLikedStatus() {
        // if (!this.winformObjHasValue()) {
        //     return;
        // }

        // winformObj.requestSetHeartLikedStatus();
    }

    public requestCameraTakeScreenshot() {
        // if (!this.winformObjHasValue()) {
        //     return;
        // }

        // winformObj.requestCameraTakeScreenshot();
    }

    public requestSetZoomLevel(clientZoomLevel: number) {
        // if (!this.winformObjHasValue()) {
        //     return;
        // }

        // winformObj.requestSetZoomLevel(clientZoomLevel);
    }

    public requestSetPnPState(position: string, expanded: boolean, timeframe: Timeframe) {
        return;
    }

    public requestMouseEnter() {
        // if (!this.winformObjHasValue()) {
        //     return;
        // }

        // winformObj.requestMouseEnter();
    }

    public requestMouseLeave() {
        // if (!this.winformObjHasValue()) {
        //     return;
        // }

        // winformObj.requestMouseLeave();
    }

    public requestSaveDrawingTools(data: Array<any>) {
        // if (!this.winformObjHasValue()) {
        //     return;
        // }


        // const dataStr = JSON.stringify(data);

        // console.log('requestSaveDrawingTools ', dataStr)
        // winformObj.requestSaveDrawingTools(dataStr);
    }

    public chartReady() {
        // if (!this.winformObjHasValue()) {
        //     return;
        // }

        // setTimeout(() => {
        //     console.log('called onChartReady');
        //     winformObj.onChartReady();
        // }, 500);
    }
}
